/**
 * Shop by Sound — audio cards + shoppable dialog (homepage showcase pattern)
 */
(function () {
  'use strict';

  function bindMobileSwiper(section) {
    if (typeof window.Swiper === 'undefined') return;

    const el = section.querySelector('[data-sbs-audio-swiper]');
    const pagination = section.querySelector('[data-sbs-audio-pagination]');
    if (!el || el.dataset.sbsSwiperBound === '1') return;
    el.dataset.sbsSwiperBound = '1';

    const mq = window.matchMedia('(max-width: 749px)');
    let swiper = null;

    const sync = () => {
      if (mq.matches) {
        if (swiper) return;
        swiper = new window.Swiper(el, {
          slidesPerView: 1,
          spaceBetween: 22,
          watchOverflow: true,
          pagination: pagination
            ? {
                el: pagination,
                clickable: true,
              }
            : undefined,
        });
      } else if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }
    };

    sync();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(sync);
    }
  }

  function pauseVideos(section) {
    section.querySelectorAll('[data-sbs-audio-dialog] video').forEach((video) => {
      try {
        video.pause();
      } catch (e) {
        // ignore
      }
    });
  }

  function playVideoAt(section, index) {
    const video = section.querySelector(`#sbs-audio-video-${section.dataset.sbsSectionId || ''}-${index + 1}`)
      || section.querySelectorAll('[data-sbs-audio-dialog] video')[index];
    if (!video) return;

    const source = video.querySelector('source');
    if (source) {
      const src = source.getAttribute('data-src');
      if (src && source.getAttribute('src') !== src) {
        source.setAttribute('src', src);
        video.load();
      }
    }

    const y = window.scrollY || window.pageYOffset || 0;
    const playPromise = video.play();
    window.scrollTo(0, y);
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => window.scrollTo(0, y)).catch(() => {});
    }
  }

  function bindDialog(section) {
    if (section.dataset.sbsDialogBound === '1') return;
    section.dataset.sbsDialogBound = '1';

    const dialog = section.querySelector('[data-sbs-audio-dialog]');
    const swiperEl = section.querySelector('[data-sbs-audio-dialog-swiper]');
    if (!dialog || !swiperEl || typeof window.Swiper === 'undefined') return;

    // Extract section id from first video id if present
    const firstVideo = dialog.querySelector('video[id^="sbs-audio-video-"]');
    if (firstVideo) {
      const match = firstVideo.id.match(/^sbs-audio-video-(.+)-\d+$/);
      if (match) section.dataset.sbsSectionId = match[1];
    }

    let scrollTop = 0;
    let scrollbarCompEls = [];

    const preventTouchScroll = (event) => {
      if (event.target.closest && event.target.closest('.swiper_product')) return;
      event.preventDefault();
    };

    const lockScroll = () => {
      scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

      document.documentElement.style.setProperty('--sbs-scrollbar-comp', `${sbw}px`);
      document.documentElement.classList.add('sbs-modal-open');
      document.body.classList.add('sbs-modal-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = sbw ? `${sbw}px` : '';

      // Keep fixed/sticky chrome from shifting when the scrollbar disappears
      scrollbarCompEls = Array.from(
        document.querySelectorAll(
          'sticky-header, .shopify-section-header-sticky, #shopify-section-header, .header-wrapper'
        )
      );
      scrollbarCompEls.forEach((el) => {
        el.style.paddingRight = sbw ? `${sbw}px` : '';
      });

      document.addEventListener('touchmove', preventTouchScroll, { passive: false });
    };

    const unlockScroll = () => {
      document.documentElement.classList.remove('sbs-modal-open');
      document.body.classList.remove('sbs-modal-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--sbs-scrollbar-comp');
      scrollbarCompEls.forEach((el) => {
        el.style.paddingRight = '';
      });
      scrollbarCompEls = [];
      document.removeEventListener('touchmove', preventTouchScroll);
      window.scrollTo(0, scrollTop);
    };

    const keepScroll = () => {
      window.scrollTo(0, scrollTop);
    };

    const dialogSwiper = new window.Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 20,
      effect: 'fade',
      autoHeight: true,
      fadeEffect: { crossFade: true },
      observer: true,
      observeParents: true,
      on: {
        slideChangeTransitionEnd: function () {
          pauseVideos(section);
          playVideoAt(section, this.activeIndex);
          keepScroll();
        },
      },
    });

    section.querySelectorAll('[data-sbs-audio-dialog-next]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dialogSwiper.slideNext();
      });
    });

    section.querySelectorAll('[data-sbs-audio-dialog-prev]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dialogSwiper.slidePrev();
      });
    });

    const openDialog = (index) => {
      lockScroll();

      dialog.hidden = false;
      dialog.style.display = 'block';
      dialogSwiper.slideTo(index, 0);
      pauseVideos(section);
      keepScroll();

      requestAnimationFrame(() => {
        playVideoAt(section, index);
        keepScroll();
        requestAnimationFrame(keepScroll);
      });
      setTimeout(keepScroll, 0);
      setTimeout(keepScroll, 100);
    };

    const closeDialog = () => {
      pauseVideos(section);
      dialog.style.display = 'none';
      dialog.hidden = true;
      unlockScroll();
    };

    section.querySelectorAll('[data-sbs-audio-open]').forEach((card) => {
      card.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const index = Number(card.getAttribute('data-index') || 0);
        openDialog(index);
      });
    });

    section.querySelectorAll('[data-sbs-audio-dialog-close]').forEach((el) => {
      el.addEventListener('click', closeDialog);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (dialog.hidden || dialog.style.display === 'none') return;
      event.preventDefault();
      closeDialog();
    });
  }

  function bindSection(section) {
    bindMobileSwiper(section);
    bindDialog(section);
  }

  function boot() {
    document.querySelectorAll('[data-sbs-audio]').forEach(bindSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.closest
      ? event.target.closest('[data-sbs-audio]') || event.target.querySelector('[data-sbs-audio]')
      : null;
    if (section) {
      section.dataset.sbsDialogBound = '';
      const swiper = section.querySelector('[data-sbs-audio-swiper]');
      if (swiper) swiper.dataset.sbsSwiperBound = '';
      bindSection(section);
    } else {
      boot();
    }
  });
})();
